import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useProduct, useRecommendations, useStockByLocation } from '../api';
import { useAppSettings } from '../contexts/SettingsContext';
import { useCart } from '../contexts/CartContext';
import { useCustomerIntelligence } from '../contexts/CustomerIntelligenceContext';
import { ProductGrid } from '../components/product/ProductGrid';
import { ImageGallery } from '../components/product/ImageGallery';
import { NotifyMe } from '../components/product/NotifyMe';
import { formatPrice, formatDiscount } from '../utils/format';
import { useAnalytics } from '../hooks/useAnalytics';
import { ProductSEO, Breadcrumb } from '../components/SEO';
import { UI_CONSTANTS } from '@petmas/shared';

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { data: product, isLoading, error } = useProduct(id ?? '');
  const { data: relatedProducts } = useRecommendations(id);
  const { addItem } = useCart();
  const { trackViewProduct, trackAddToCart } = useAnalytics();
  const { settings } = useAppSettings();
  const { trackProductView, trackAddToCart: trackCartIntelligence } = useCustomerIntelligence();

  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  // Get the current variant ID for stock lookup
  const currentVariantId = selectedVariant ?? product?.variants?.[0]?.id;

  // Fetch stock by location if feature is enabled
  const { data: stockData } = useStockByLocation(
    settings?.features?.stockByLocationEnabled ? currentVariantId : undefined
  );

  // Track product view for analytics and customer intelligence (AI personalization)
  useEffect(() => {
    if (product) {
      trackViewProduct(product);
      // Track for AI personalization
      trackProductView(product.id, {
        name: product.name,
        category: product.category,
        brand: product.brand,
        price: product.price,
      });
    }
  }, [product, trackViewProduct, trackProductView]);

  if (isLoading) {
    return <ProductDetailSkeleton />;
  }

  if (error || !product) {
    return (
      <div className="container py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Producto no encontrado</h1>
        <p className="mt-2 text-gray-600">
          El producto que buscas no existe o fue eliminado.
        </p>
        <Link to="/" className="mt-4 btn btn-primary inline-block">
          Volver al inicio
        </Link>
      </div>
    );
  }

  const variant = selectedVariant
    ? product.variants?.find((v) => v.id === selectedVariant)
    : product.variants?.[0];

  const isOutOfStock = variant ? variant.stockAvailable <= 0 : true;
  const hasDiscount = product.discountPercent && product.discountPercent > 0;

  const handleAddToCart = () => {
    if (!variant) return;

    addItem(
      {
        productId: product.id,
        variantId: variant.id,
        productName: product.name,
        variantName: variant.description,
        price: product.price,
        imageUrl: product.imageUrl,
      },
      quantity
    );

    // Track add to cart event for analytics
    trackAddToCart(product, quantity);
    // Track for AI personalization
    trackCartIntelligence(product.id, variant.id, quantity);
  };

  // Build canonical URL
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${location.pathname}`
    : undefined;

  // Build breadcrumb items for schema
  const breadcrumbItems = [
    { name: 'Inicio', url: typeof window !== 'undefined' ? window.location.origin : '/' },
    { name: product.category ?? 'Productos', url: typeof window !== 'undefined' ? `${window.location.origin}/${product.category}` : `/${product.category}` },
    { name: product.name, url: canonicalUrl ?? location.pathname },
  ];

  return (
    <div className="bg-white">
      {/* SEO with Product schema */}
      <ProductSEO
        product={product}
        breadcrumbs={breadcrumbItems}
        canonical={canonicalUrl}
      />

      {/* Breadcrumb with schema */}
      <div className="container py-4">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main content */}
      <div className="container pb-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div>
            <ImageGallery
              images={product.images ?? (product.imageUrl ? [product.imageUrl] : [])}
              productName={product.name}
            />
          </div>

          {/* Product info */}
          <div>
            {/* Brand */}
            {product.brand && (
              <p className="text-sm text-gray-500 uppercase tracking-wide">
                {product.brand}
              </p>
            )}

            {/* Name */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
              {product.name}
            </h1>

            {/* Price */}
            <div className="mt-4 flex items-baseline gap-3">
              <span className={`text-3xl font-bold ${hasDiscount ? 'text-red-500' : 'text-gray-900'}`}>
                {formatPrice(product.price)}
              </span>
              {hasDiscount && product.originalPrice && (
                <>
                  <span className="text-lg text-gray-400 line-through">
                    {formatPrice(product.originalPrice)}
                  </span>
                  <span className="badge badge-discount">
                    {formatDiscount(product.discountPercent!)}
                  </span>
                </>
              )}
            </div>

            {/* Stock status */}
            <div className="mt-4">
              {isOutOfStock ? (
                <span className="text-red-500 font-medium">Agotado</span>
              ) : (
                <span className="text-green-600 font-medium">
                  {variant && variant.stockAvailable > UI_CONSTANTS.SIMILAR_PRODUCTS_LIMIT
                    ? 'En stock'
                    : `Solo ${variant?.stockAvailable} disponibles`}
                </span>
              )}

              {/* Stock by location */}
              {settings?.features?.stockByLocationEnabled && stockData && stockData.byLocation.length > 0 && (
                <div className="mt-3 text-sm text-gray-600">
                  <p className="font-medium text-gray-700 mb-1">Disponibilidad por tienda:</p>
                  <ul className="space-y-1">
                    {stockData.byLocation.map((location) => (
                      <li key={location.officeId} className="flex justify-between">
                        <span>{location.officeName}</span>
                        <span className={location.quantityAvailable > 0 ? 'text-green-600' : 'text-red-500'}>
                          {location.quantityAvailable > 0
                            ? `${location.quantityAvailable} unidades`
                            : 'Agotado'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Notify Me for out of stock */}
              {isOutOfStock && settings?.features?.notifyMeEnabled && variant && (
                <NotifyMe
                  variantId={variant.bsaleId}
                  productId={product.id}
                  productName={product.name}
                />
              )}
            </div>

            {/* Variants */}
            {product.variants && product.variants.length > 1 && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Variante
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVariant(v.id)}
                      disabled={v.stockAvailable <= 0}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        (selectedVariant ?? product.variants?.[0]?.id) === v.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : v.stockAvailable <= 0
                            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                            : 'border-gray-300 hover:border-primary'
                      }`}
                    >
                      {v.description}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Cantidad
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center border rounded-lg hover:bg-gray-50"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  max={variant?.stockAvailable ?? UI_CONSTANTS.MAX_CART_DISPLAY}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 h-10 text-center border rounded-lg"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={variant && quantity >= variant.stockAvailable}
                  className="w-10 h-10 flex items-center justify-center border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  +
                </button>
              </div>
            </div>

            {/* Add to cart */}
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className="btn btn-primary flex-1 py-3"
              >
                {isOutOfStock ? 'Agotado' : 'Agregar al Carrito'}
              </button>
              <button
                className="btn btn-outline p-3"
                aria-label="Agregar a favoritos"
              >
                <HeartIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Features */}
            <div className="mt-8 space-y-3">
              <Feature icon="🚚" text="Envio gratis en pedidos mayores a S/150" />
              <Feature icon="🔄" text="30 dias para devoluciones" />
              <Feature icon="✅" text="Garantia de calidad" />
            </div>

            {/* Description */}
            {(product.description || product.webDescription) && (
              <div className="mt-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  Descripcion
                </h2>
                <div className="prose prose-sm text-gray-600">
                  {product.webDescription ?? product.description}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related products */}
      {relatedProducts && relatedProducts.length > 0 && (
        <section className="bg-gray-50 py-12">
          <div className="container">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Productos Relacionados
            </h2>
            <ProductGrid products={relatedProducts.slice(0, UI_CONSTANTS.RELATED_PRODUCTS_LIMIT)} />
          </div>
        </section>
      )}
    </div>
  );
}

function Feature({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-600">
      <span className="text-lg">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function ProductDetailSkeleton() {
  return (
    <div className="container py-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        <div className="aspect-square bg-gray-200 rounded-xl animate-pulse" />
        <div className="space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse" />
          <div className="h-8 bg-gray-200 rounded w-3/4 animate-pulse" />
          <div className="h-10 bg-gray-200 rounded w-1/3 animate-pulse" />
          <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse" />
          <div className="h-12 bg-gray-200 rounded animate-pulse" />
          <div className="h-32 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );
}
